import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Globe } from "lucide-react";

interface SiiWebCredentialsProps {
  hasSiiWebCredentials: boolean;
  onSave: (rut: string, password: string) => Promise<void>;
  isSaving?: boolean;
}

export function SiiWebCredentials({
  hasSiiWebCredentials,
  onSave,
  isSaving,
}: SiiWebCredentialsProps) {
  const [rut, setRut] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!rut || !password) {
      setError("Ingrese el RUT y la clave tributaria");
      return;
    }
    setError(null);
    try {
      await onSave(rut, password);
      setRut("");
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar credenciales");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Credenciales Portal SII
        </CardTitle>
        <CardDescription>
          RUT y clave del portal SII para sincronizar el Registro de Compras y Ventas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          {hasSiiWebCredentials ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="font-medium">Credenciales configuradas</p>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-destructive" />
              <div>
                <p className="font-medium">Sin credenciales web</p>
                <p className="text-sm text-muted-foreground">
                  Requeridas para sincronizar libros de compras y ventas
                </p>
              </div>
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sii-web-rut">RUT</Label>
          <Input
            id="sii-web-rut"
            placeholder="12345678-9"
            value={rut}
            onChange={(e) => setRut(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="sii-web-password">Clave SII</Label>
          <Input
            id="sii-web-password"
            type="password"
            placeholder="Ingrese la clave"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!rut || !password || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              hasSiiWebCredentials ? "Actualizar Credenciales" : "Guardar Credenciales"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
